/**
 * MosaicGroupingModal.js — v6.32.164
 *
 * Generates suggested instructional groups for a class via
 * POST /api/mosaic/grouping/suggest. Three strategies:
 *   - by_composite          (k buckets along the 1-5 composite axis)
 *   - by_weakest_domain     (cluster by lowest-scoring domain)
 *   - newcomer_sife         (single small-group recommendation)
 */
import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Loader2, Users } from 'lucide-react';
import MosaicBucketBadge from './MosaicBucketBadge';
import MosaicStudentFlags from './MosaicStudentFlags';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STRATEGIES = [
  { v: 'by_composite',       label: 'By Composite Level',   desc: 'Equal-width buckets along the 1–5 MOSAIC ladder' },
  { v: 'by_weakest_domain',  label: 'By Weakest Domain',    desc: 'Cluster by Listening / Speaking / Reading / Writing weakness' },
  { v: 'newcomer_sife',      label: 'Newcomer + SIFE',      desc: 'Pull newcomer and SIFE students into one priority small group' },
];

const MosaicGroupingModal = ({ open, onOpenChange, classId }) => {
  const [strategy, setStrategy] = useState('by_composite');
  const [groupCount, setGroupCount] = useState(4);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  const run = async () => {
    if (!classId) { toast.error('No class selected'); return; }
    setLoading(true);
    setData(null);
    try {
      const r = await axios.post(`${API}/mosaic/grouping/suggest`, {
        class_id: classId,
        strategy,
        group_count: groupCount,
      });
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to compute groups');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="mosaic-grouping-modal" className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            <Users className="w-5 h-5 text-violet-600" />
            MOSAIC Grouping Engine
          </DialogTitle>
          <DialogDescription>
            Generates suggested small groups for differentiated instruction based on intake data.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            {STRATEGIES.map((s) => (
              <button
                key={s.v}
                type="button"
                onClick={() => setStrategy(s.v)}
                data-testid={`grouping-strategy-${s.v}`}
                className={`text-left p-3 border-2 transition-colors ${
                  strategy === s.v
                    ? 'border-violet-600 bg-violet-50'
                    : 'border-gray-300 hover:border-gray-400'
                }`}
              >
                <div className="font-bold text-sm">{s.label}</div>
                <div className="text-xs text-gray-600 mt-0.5">{s.desc}</div>
              </button>
            ))}
          </div>

          {strategy === 'by_composite' && (
            <div className="flex items-center gap-3">
              <label className="text-sm font-semibold">Number of groups:</label>
              <select
                value={groupCount}
                onChange={(e) => setGroupCount(Number(e.target.value))}
                className="h-9 border border-gray-300 px-2 text-sm"
                data-testid="grouping-group-count"
              >
                {[2, 3, 4, 5].map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
            </div>
          )}

          <div className="flex justify-end">
            <Button
              onClick={run}
              disabled={loading || !classId}
              className="bg-violet-600 hover:bg-violet-700 text-white"
              data-testid="grouping-run-btn"
            >
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Suggest Groups
            </Button>
          </div>

          {data && (
            <div className="space-y-3" data-testid="grouping-results">
              <div className="text-xs text-gray-500">
                {data.student_count} students · strategy: <strong>{data.strategy}</strong>
                {data.newcomer_sife_count > 0 && (
                  <span className="ml-2 text-violet-700">· {data.newcomer_sife_count} newcomer/SIFE</span>
                )}
              </div>
              {(data.groups || []).map((g) => (
                <div key={g.id} className="border-2 border-gray-300" data-testid={`grouping-group-${g.id}`}>
                  <div className="flex items-center justify-between px-3 py-2 bg-gray-900 text-white">
                    <div className="flex items-center gap-2">
                      <span className="font-bold text-sm" style={{ fontFamily: 'Outfit, sans-serif' }}>
                        {g.label}
                      </span>
                      {g.level && <MosaicBucketBadge level={g.level} compact />}
                    </div>
                    <span className="text-xs text-gray-300">{g.size} students</span>
                  </div>
                  {g.rationale && (
                    <div className="px-3 py-2 bg-violet-50 text-xs text-violet-900 border-b border-violet-200">
                      {g.rationale}
                    </div>
                  )}
                  <ul className="divide-y divide-gray-100">
                    {(g.members || []).slice(0, 30).map((m) => (
                      <li key={m.student_id} className="px-3 py-1.5 text-sm flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className="font-semibold">{m.name}</span>
                          <MosaicStudentFlags intake={m} compact />
                          {m.weakest_domain && (
                            <span className="text-[10px] uppercase text-gray-500 tracking-wider">
                              weak: {m.weakest_domain}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          {m.composite_level ? <MosaicBucketBadge level={m.composite_level} compact /> : null}
                          <span className="text-xs tabular-nums text-gray-500">{(m.composite || 0).toFixed(2)}</span>
                        </div>
                      </li>
                    ))}
                    {(g.members || []).length === 0 && (
                      <li className="px-3 py-2 text-xs text-gray-400 italic">No students in this group</li>
                    )}
                  </ul>
                </div>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default MosaicGroupingModal;
